import { Link } from 'react-router-dom'
import type {
  AnyLandingPageContent,
  CalculatorAxis,
  LandingPageCta,
  LandingPageLink,
  ProgrammaticLandingPageConfig,
} from '../../content/landingPages/types'
import type { SupportedLanguage } from '../../i18n'
import { useLandingPageSeo } from '../../seo/useLandingPageSeo'
import { PROGRAMMATIC_LANDING_PAGES } from '../../content/landingPages'
import { getNeighborProgrammaticPages } from '../../content/landingPages/relatedPages'
import { MarketingHeader } from './MarketingHeader'
import { MarketingFooter } from './MarketingFooter'
import { useMarketingHeaderActions } from './useMarketingHeaderActions'
import { PvKostenRechnerWidget, type PvKostenRechnerWidgetProps } from './PvKostenRechnerWidget'
import { usePriceCalculatorCta } from './usePriceCalculatorCta'
import { CityYieldChart } from './CityYieldChart'

type HeaderActions = ReturnType<typeof useMarketingHeaderActions>

/** Maps a pSEO Config's calculatorPrefill (Task 01) onto the widget's flat initialKwp/
 *  initialStorageKwh props (Task 02) - kept here rather than on the widget itself so
 *  PvKostenRechnerWidget stays decoupled from the landing-page content types. A 'storage'-only
 *  axis (no kwp) has no default system size yet - Task 05 builds those pages and picks a real
 *  size; auto-triggering here would mean inventing a "typical" kWp value with no data behind it
 *  (CLAUDE.md-Pflicht: keine erfundenen Zahlen), so the widget is left interactive/empty for that
 *  case, same as if no calculatorPrefill were given at all. */
function calculatorPrefillProps(axis: CalculatorAxis): Pick<PvKostenRechnerWidgetProps, 'initialKwp' | 'initialStorageKwh'> {
  switch (axis.kind) {
    case 'kwp':
      return { initialKwp: axis.kwp }
    case 'kwp+storage':
      return { initialKwp: axis.kwp, initialStorageKwh: axis.kwh }
    case 'storage':
      return {}
  }
}

/** SEO-4.0-Story Task 03 (docs/tasks/active/seo/seo-4.0/03-automatisierte-interne-verlinkung.md):
 *  `getNeighborProgrammaticPages` (pSEO-Story Task 06) existed since the previous story but was
 *  never actually rendered - only used in a test asserting the hand-curated `relatedLinks`
 *  happened to include a neighbor. This computes the real size-neighbor links for a programmatic
 *  page and merges them into the hand-curated list (deduped by href) instead of replacing it -
 *  the manual list also carries thematic links (pillar page, blog, calculator) that the neighbor
 *  computation knows nothing about, so a full replacement would lose those. Static pages (no
 *  `calculatorPrefill`) get no computed neighbors, same as before. */
function withComputedNeighbors(content: AnyLandingPageContent): LandingPageLink[] {
  if (!content.calculatorPrefill) return content.relatedLinks
  const neighborLinks: LandingPageLink[] = getNeighborProgrammaticPages(
    content as ProgrammaticLandingPageConfig,
    PROGRAMMATIC_LANDING_PAGES,
  ).map((page) => ({ label: page.h1, href: `/${page.slug}` }))
  const existingHrefs = new Set(content.relatedLinks.map((link) => link.href))
  return [...content.relatedLinks, ...neighborLinks.filter((link) => !existingHrefs.has(link.href))]
}

/** Chrome around the page-specific content (FAQ heading, related-links nav) - not part of the
 *  translated content objects themselves since it's generic UI copy, not page content. */
const FAQ_TITLE: Record<SupportedLanguage, string> = {
  de: 'Häufige Fragen',
  es: 'Preguntas frecuentes',
  pl: 'Najczęściej zadawane pytania',
}
const RELATED_LINKS_TITLE: Record<SupportedLanguage, string> = {
  de: 'Weiterführende Themen',
  es: 'Temas relacionados',
  pl: 'Powiązane tematy',
}

// GitLab-Issues #143/#144: field labels for the numbered step-by-step guides - generic UI chrome
// like FAQ_TITLE/RELATED_LINKS_TITLE above, not page content, so kept here rather than repeated
// per HowToStep object in every one of the 6 (2 audiences x 3 languages) content files.
const STEP_LABELS: Record<SupportedLanguage, { seeIt: string; doIt: string; then: string; success: string; trouble: string }> = {
  de: { seeIt: 'Was Sie sehen', doIt: 'Was Sie tun', then: 'Was danach passiert', success: 'Erfolgreich, wenn', trouble: 'Falls es nicht klappt' },
  es: { seeIt: 'Qué verá', doIt: 'Qué hacer', then: 'Qué ocurre después', success: 'Ha funcionado si', trouble: 'Si algo falla' },
  pl: { seeIt: 'Co widzisz', doIt: 'Co zrobić', then: 'Co dzieje się dalej', success: 'Sukces, gdy', trouble: 'Jeśli coś nie działa' },
}

function CtaButton({ cta, headerActions, className }: { cta: LandingPageCta; headerActions: HeaderActions; className: string }) {
  if (cta.action === 'link') {
    return (
      <Link to={cta.href!} className={className}>
        {cta.label}
      </Link>
    )
  }
  const onClick = cta.action === 'customer' ? headerActions.onSelectCustomer : headerActions.onLoginCraftsman
  return (
    <button type="button" className={className} onClick={onClick}>
      {cta.label}
    </button>
  )
}

/** Shared renderer for the SEO landing pages registered in content/landingPages - top-level
 *  bare-URL routes like /blog and /pv-rechner (see content/landingPages/types.ts), one per
 *  (topic, language) pair. One component instead of a bespoke page per language/topic, following
 *  the content-object + shared-renderer pattern already used for the blog (see BlogPostPage).
 *
 *  Since pSEO-Story Task 02, also renders the PvKostenRechnerWidget (pre-filled from
 *  content.calculatorPrefill, see calculatorPrefillProps) directly after the intro/hero, before
 *  the rest of the content sections - the 21 hand-written static pages never set
 *  calculatorPrefill, so the slot simply doesn't render for them, unchanged from before.
 *
 *  Unlike the page's own prose (plain strings picked by content.lang, never i18next), the widget
 *  and shared marketing chrome use i18next. App's central RouteLocaleSynchronizer keeps that
 *  global language aligned with this page's URL-derived registry locale. */
export function SeoLandingPage({ content }: { content: AnyLandingPageContent }) {
  useLandingPageSeo(content)
  const headerActions = useMarketingHeaderActions()
  const handleCalculatorRequestOffer = usePriceCalculatorCta(headerActions.onSelectCustomer)
  const relatedLinks = withComputedNeighbors(content)

  return (
    <>
      <MarketingHeader {...headerActions} />
      <article className="blog-page seo-landing-page">
        <p className="eyebrow">{content.eyebrow}</p>
        <h1>{content.h1}</h1>
        <p className="pv-rechner-intro">{content.intro}</p>

        {content.calculatorPrefill && (
          <PvKostenRechnerWidget
            onRequestOffer={handleCalculatorRequestOffer}
            {...calculatorPrefillProps(content.calculatorPrefill)}
          />
        )}

        {/* SEO-4.0-Story Task 08: city-yield pages have no calculatorPrefill (see types.ts doc on
            CalculatorAxis) and instead show this small monthly-yield chart + rank summary. */}
        {content.cityYield && <CityYieldChart data={content.cityYield} />}

        {/* GitLab-Issues #143/#144: numbered step-by-step guide, rendered as an <ol> (not the
            generic `sections` above) since each step needs several distinct labeled sub-fields
            (Was sehe ich/tue ich/passiert danach/Erfolg/Troubleshooting) plus an optional
            screenshot, not just a heading/paragraph/list block. Only the two how-to pages set
            `steps`; every other landing page renders nothing here, unchanged from before. */}
        {content.steps && content.steps.length > 0 && (
          <ol className="howto-steps">
            {content.steps.map((step, i) => (
              <li key={step.title} className="howto-step">
                <span className="step-number">{i + 1}</span>
                <h2>{step.title}</h2>
                {step.screenshot && (
                  <img
                    className="howto-step-screenshot"
                    src={step.screenshot.src}
                    width={step.screenshot.width}
                    height={step.screenshot.height}
                    loading="lazy"
                    alt={step.screenshot.alt}
                  />
                )}
                <p>
                  <strong>{STEP_LABELS[content.lang].seeIt}:</strong> {step.whatYouSee}
                </p>
                <p>
                  <strong>{STEP_LABELS[content.lang].doIt}:</strong> {step.whatToDo}
                </p>
                <p>
                  <strong>{STEP_LABELS[content.lang].then}:</strong> {step.whatHappensNext}
                </p>
                <p>
                  <strong>{STEP_LABELS[content.lang].success}:</strong> {step.successCriterion}
                </p>
                {step.troubleshooting && (
                  <p className="howto-step-trouble">
                    <strong>{STEP_LABELS[content.lang].trouble}:</strong> {step.troubleshooting}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}

        {content.sections.map((section, i) => {
          if (section.type === 'heading') return <h2 key={i}>{section.text}</h2>
          if (section.type === 'list')
            return (
              <ul key={i}>
                {section.items?.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )
          return <p key={i}>{section.text}</p>
        })}

        <p className="blog-post-cta seo-landing-cta">
          <CtaButton cta={content.primaryCta} headerActions={headerActions} className="btn-primary" />
          {content.secondaryCta && (
            <CtaButton cta={content.secondaryCta} headerActions={headerActions} className="btn-secondary" />
          )}
        </p>

        {content.faq && content.faq.length > 0 && (
          <section className="faq seo-landing-faq">
            <h2 className="section-title">{FAQ_TITLE[content.lang]}</h2>
            <div className="faq-list">
              {content.faq.map((item) => (
                <details key={item.q} className="faq-item">
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {relatedLinks.length > 0 && (
          <nav className="seo-landing-related" aria-label={RELATED_LINKS_TITLE[content.lang]}>
            <h2>{RELATED_LINKS_TITLE[content.lang]}</h2>
            <ul>
              {relatedLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href}>{link.label} →</Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </article>
      <MarketingFooter />
    </>
  )
}
